import React, { useState, useEffect } from "react";
import axios from "axios";
import { fetchApprovedInsights } from "../../services/mediaApi";
import { optimizeCloudinaryUrl } from "../../utils/cloudinaryUrl";
import "./Newsletter.css";
import "../ServiceDetail/ServiceDetail.css"
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import TextType from "../Home/TextType";
import SplitText from "../SplitText/SplitText";
import { ArrowLeft, Trash2, ShieldCheck, LogOut, FileText, User } from "lucide-react";

export default function Newsletter() {
    const navigate = useNavigate();
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminDetails, setAdminDetails] = useState(null);

    const [pendingCount, setPendingCount] = useState(0);

    useEffect(() => {
        loadInsights();
        // Check if user was already admin in this session
        const adminStatus = sessionStorage.getItem("tts_admin") === "true";
        setIsAdmin(adminStatus);
        
        if (adminStatus) {
            const details = JSON.parse(sessionStorage.getItem("tts_admin_details"));
            setAdminDetails(details);
            checkPendingInsights();
        }
    }, []);

    const checkPendingInsights = async () => {
        try {
            const response = await axios.get("/api/insights/all");
            // Handle both array and object responses
            const data = Array.isArray(response.data) ? response.data : (response.data.insights || []);
            const pending = data.filter(i => i.status?.toLowerCase() === "pending");
            setPendingCount(pending.length);
        } catch (error) {
            console.error("Failed to check pending insights:", error);
        }
    };

    const loadInsights = async () => {
        setLoading(true);
        try {
            const { insights: data } = await fetchApprovedInsights();
            setInsights(data);
        } catch (error) {
            console.error("Failed to fetch insights:", error);
        } finally {
            setLoading(false);
        }
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return "https://placehold.co/400x250?text=No+Image+Path";
        return optimizeCloudinaryUrl(imagePath, 1200);
    };

    const handleCardClick = (item) => {
        navigate(`/newsletter/${item._id}`);
    };

    const toggleAdmin = () => {
        if (!isAdmin) {
            const pass = prompt("Enter Admin Password:");
            if (pass === "admintts@07") { // Updated password
                setIsAdmin(true);
                sessionStorage.setItem("tts_admin", "true");
                
                // Add default admin details if they don't exist
                const defaultDetails = {
                    name: "TTS Administrator",
                    email: "business@triothoughtsolution.com",
                    role: "Super Admin",
                    accessLevel: "Full Access"
                };
                sessionStorage.setItem("tts_admin_details", JSON.stringify(defaultDetails));
                setAdminDetails(defaultDetails);
                
                loadInsights(); // Reload to show pending insights

                if (window.confirm("Admin Login Successful! Would you like to go to the Admin Dashboard to review pending insights?")) {
                    navigate("/admin-dashboard");
                }
            } else {
                alert("Incorrect password!");
            }
        } else {
            setIsAdmin(false);
            sessionStorage.removeItem("tts_admin");
            sessionStorage.removeItem("tts_admin_details");
            setAdminDetails(null);
            loadInsights(); // Reload to hide pending insights
        }
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation(); // Prevent card click
        if (window.confirm("Are you sure you want to remove this insight?")) {
            try {
                await axios.delete(`/api/insights/${id}`);
                // Update local state immediately
                setInsights(prev => prev.filter(i => i._id !== id));
                loadInsights(); // Refresh the list from server
            } catch (error) {
                console.error("Failed to delete insight:", error);
                alert("Failed to delete insight. Please try again.");
            }
        }
    };

    return (
        <div className="service-page-wrapper">
            <div className="admin-actions">
                {isAdmin && pendingCount > 0 && (
                    <motion.div 
                        className="pending-badge-float"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        onClick={() => navigate("/admin-dashboard")}
                    >
                        <FileText size={16} />
                        <span>{pendingCount} Pending</span>
                    </motion.div>
                )}
                <button 
                    className={`admin-toggle-btn ${isAdmin ? 'active' : ''}`} 
                    onClick={toggleAdmin}
                    title={isAdmin ? "Logout Admin" : "Admin Login"}
                >
                    {isAdmin ? <LogOut size={20} /> : <ShieldCheck size={20} />}
                </button>
            </div>
            {/* ── HERO BANNER ── */}
            <section className="main-hero">
                <h4 className="main-hero-sub">TTS INSIGHTS</h4>
                <h1 className="main-hero-title">
                    <TextType
                        text={["Stay Updated with TTS Insights"]}
                        typingSpeed={75}
                        pauseDuration={2000}
                        deletingSpeed={50}
                        showCursor={true}
                        cursorCharacter="|"
                        loop={true}
                    />
                </h1>
            </section>

            <section className="service-inpage">
                <div className="container">
                    <div className="newsletter-top-header">
                        <div className="section-header-centered">
                            <SplitText
                                text="TTS Insights"
                                className="heading-unified"
                                tag="h2"
                                delay={50}
                                duration={0.8}
                                ease="power3.out"
                                splitType="chars"
                                threshold={0.1}
                            />
                            <div className="heading-underline"></div>
                        </div>
                        <button className="back-home-btn small-btn" onClick={() => navigate("/open")}>
                            Add
                            <ArrowLeft size={18} style={{ transform: 'rotate(180deg)' }} />
                        </button>
                    </div>

                    {/* Insights Grid (Showing only uploaded images) */}
                    <div className="newsletter-grid dynamic-only">
                        {loading ? (
                            <div className="no-insights-placeholder">
                                <p>Loading insights...</p>
                            </div>
                        ) : insights.length > 0 ? (
                            insights.map((item, index) => (
                                <motion.div 
                                    key={item._id}
                                    className="newsletter-card image-only clickable"
                                    onClick={() => handleCardClick(item)}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                >
                                    {isAdmin && (
                                        <div className="admin-card-controls">
                                            {item.status?.toLowerCase() === "pending" && (
                                                <div className="card-status-badge pending">PENDING</div>
                                            )}
                                            <button 
                                                className="delete-card-btn" 
                                                onClick={(e) => handleDelete(e, item._id)}
                                                title="Remove this picture"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    )}
                                    <div className="newsletter-card-image">
                                        <img 
                                            src={getImageUrl(item.image)} 
                                            alt={item.title} 
                                            width="400" 
                                            height="250" 
                                            loading="lazy" 
                                            onError={(e) => {
                                                if (!e.target.src.includes('placehold.co')) {
                                                    const failedUrl = e.target.src;
                                                    console.warn(`Newsletter image failed to load: ${failedUrl}`);
                                                    
                                                    // Use a professional placeholder instead of error text
                                                    e.target.src = "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=400&h=250&auto=format&fit=crop";
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="newsletter-card-footer">
                                        <div className="insight-card-main-info">
                                            <h3>{item.title}</h3>
                                            <div className="insight-author">
                                                <User size={14} />
                                                <span>{item.author?.name || "Anonymous"}</span>
                                            </div>
                                        </div>
                                        <span className={`status-tag ${(typeof item.status === 'string' ? item.status : 'Pending').toLowerCase()}`}>
                                            {typeof item.status === 'string' ? item.status : "Pending"}
                                        </span>
                                    </div>
                                </motion.div>
                            ))
                        ) : (
                            <div className="no-insights-placeholder">
                                <p>No insights published yet. Click "Add" to submit your first insight for approval!</p>
                            </div>
                        )}
                    </div>

                    {isAdmin && adminDetails && (
                        <motion.div 
                            className="admin-footer-details"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="admin-details-card">
                                <div className="admin-details-header">
                                    <ShieldCheck size={24} color="#7c5dfa" />
                                    <h3>Administrator Profile</h3>
                                </div>
                                <div className="admin-details-grid">
                                    <div className="admin-detail-item">
                                        <span className="label">Name</span>
                                        <span className="value">{adminDetails.name}</span>
                                    </div>
                                    <div className="admin-detail-item">
                                        <span className="label">Email</span>
                                        <span className="value">{adminDetails.email}</span>
                                    </div>
                                    <div className="admin-detail-item">
                                        <span className="label">Role</span>
                                        <span className="value role-badge">{adminDetails.role}</span>
                                    </div>
                                    <div className="admin-detail-item">
                                        <span className="label">Status</span>
                                        <span className="value status-active">Authenticated</span>
                                    </div>
                                </div>
                                <button className="go-to-admin-btn" onClick={() => navigate("/admin-dashboard")}>
                                    Go to Admin Dashboard
                                </button>
                            </div>
                        </motion.div>
                    )}
                </div>

                <div className="back-home-dual-wrapper">
                    <button className="back-home-btn" onClick={() => navigate("/")}>
                        <ArrowLeft size={20} />
                        Back to Home Page
                    </button>
                </div>
            </section>
        </div>
    );
}
